/**
 * useDashboardMetrics - Shared state hook for the three Dashboard
 * tier surfaces (super_admin / manager / tech), backed by TanStack Query.
 *
 * Owns:
 *   - Filter state (period from/to + comparison + manager-tier scope)
 *   - The latest metrics payload from /dashboard/metrics (cached + persisted)
 *   - Loading + error flags for inline UI feedback
 *
 * The query key includes the filter object, so changing the date range / scope
 * automatically fetches (and caches) that variant. Switching back to a
 * previously-viewed range is instant from cache.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    dashboardService,
    DashboardFilters,
    DashboardMetrics,
} from '../services/dashboardService';
import { qk } from '../lib/queryKeys';

/* ============================================
 * DEFAULTS
 * ============================================ */

/**
 * Default filter — empty object, which the server interprets as
 * "current calendar month + previous month comparison + 'company'
 * scope". Exposed so callers can reset to default cleanly.
 */
export const DEFAULT_DASHBOARD_FILTERS: DashboardFilters = {};

/* ============================================
 * HOOK
 * ============================================ */

export interface UseDashboardMetricsResult {
    filters: DashboardFilters;
    setFilters: (next: DashboardFilters) => void;
    metrics: DashboardMetrics | null;
    isLoading: boolean;
    error: string | null;
    refresh: () => void;
}

/**
 * Fetch + unwrap the metrics payload. api.ts wraps responses in
 * { data, status, ok } and the server wraps payloads in { data: ... } —
 * both layers camelize, so the actual metrics live at res.data.data.
 */
async function fetchDashboardMetrics(filters: DashboardFilters): Promise<DashboardMetrics | null> {
    const res = await dashboardService.metrics(filters);
    return ((res.data as any)?.data ?? null) as DashboardMetrics | null;
}

export function useDashboardMetrics(): UseDashboardMetricsResult {
    const [filters, setFilters] = useState<DashboardFilters>(DEFAULT_DASHBOARD_FILTERS);

    const query = useQuery({
        queryKey: [...qk.dashboard.all, 'metrics', filters],
        queryFn: () => fetchDashboardMetrics(filters),
    });

    return {
        filters,
        setFilters,
        metrics: query.data ?? null,
        isLoading: query.isLoading,
        error: query.error ? (query.error as Error).message : null,
        refresh: () => {
            query.refetch();
        },
    };
}
